<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed inset-0 z-[3500]" @close="close">
      <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div class="flex min-h-full items-center justify-center">
          <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 translate-y-3 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-3 sm:scale-95">
            <DialogPanel class="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white p-6 shadow-2xl">
              <div v-if="initializing" class="flex min-h-[300px] flex-col items-center justify-center px-8 text-center" role="status" aria-live="polite">
                <span class="h-11 w-11 animate-spin rounded-full border-4 border-blue-100 border-t-[#2D8CFF]" />
                <p class="mt-5 text-lg font-semibold text-zinc-900">Preparing secure card entry…</p>
                <p class="mt-2 text-sm text-zinc-500">Platforma is checking the available merchant accounts.</p>
              </div>

              <template v-else>
                <div v-if="provider === 'stripe' && submitting" class="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/95 px-8 text-center backdrop-blur-sm" role="status" aria-live="assertive">
                  <span class="h-11 w-11 animate-spin rounded-full border-4 border-blue-100 border-t-[#2D8CFF]" />
                  <p class="mt-5 text-lg font-semibold text-zinc-900">Securing your payment method…</p>
                  <p class="mt-2 max-w-sm text-sm leading-6 text-zinc-500">Please keep this window open while Stripe securely verifies and saves your card.</p>
                </div>

                <CreatorBillingBoaForm v-if="provider === 'boa'" @close="close" @saved="handleBoaSaved" />
                <template v-else>
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <DialogTitle class="text-lg font-semibold text-zinc-900">Add or update payment method</DialogTitle>
                      <p class="mt-1 text-sm text-zinc-500">Card details are entered securely with Stripe and are never stored by Platforma.</p>
                    </div>
                    <button type="button" class="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700" @click="close">
                      <XMarkIcon class="h-5 w-5" />
                      <span class="sr-only">Close</span>
                    </button>
                  </div>

                  <div class="mt-6 space-y-4">
                    <label class="block">
                      <span class="text-sm font-medium text-zinc-700">Name on card</span>
                      <input v-model.trim="cardholderName" type="text" autocomplete="cc-name" class="mt-1 block h-11 w-full rounded-lg border-zinc-300 text-sm shadow-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF]">
                    </label>
                    <div>
                      <span class="text-sm font-medium text-zinc-700">Card number</span>
                      <div class="mt-1 h-11 rounded-lg border border-zinc-300 px-3 pt-3"><div ref="cardNumberMount" /></div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <span class="text-sm font-medium text-zinc-700">Expiration</span>
                        <div class="mt-1 h-11 rounded-lg border border-zinc-300 px-3 pt-3"><div ref="cardExpiryMount" /></div>
                      </div>
                      <div>
                        <span class="text-sm font-medium text-zinc-700">CVC</span>
                        <div class="mt-1 h-11 rounded-lg border border-zinc-300 px-3 pt-3"><div ref="cardCvcMount" /></div>
                      </div>
                    </div>
                    <p v-if="errorMessage" class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{{ errorMessage }}</p>
                  </div>

                  <div class="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button type="button" class="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50" :disabled="submitting" @click="close">Cancel</button>
                    <button type="button" class="rounded-lg bg-[#2D8CFF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1f78df] disabled:cursor-not-allowed disabled:opacity-60" :disabled="submitting || !ready" @click="saveCard">
                      {{ submitting ? 'Saving…' : 'Save payment method' }}
                    </button>
                  </div>
                </template>
              </template>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import CreatorBillingBoaForm from '@/Components/CreatorBillingBoaForm.vue';

const props = defineProps({ open: { type: Boolean, default: false } });
const emit = defineEmits(['update:open', 'saved']);
const apiBase = `${process.env.VUE_APP_API_PROXY_URL || ''}/api/platforma-creator-billing`;
const cardholderName = ref('');
const errorMessage = ref('');
const submitting = ref(false);
const initializing = ref(false);
const ready = ref(false);
const provider = ref('stripe');
const cardNumberMount = ref(null);
const cardExpiryMount = ref(null);
const cardCvcMount = ref(null);
let stripe = null;
let elements = null;
let cardNumber = null;
let cardExpiry = null;
let cardCvc = null;
let setupIntentId = '';
let clientSecret = '';
let stripeScriptPromise = null;

const requestJson = async (path, options = {}) => {
  const response = await fetch(`${apiBase}${path}`, { credentials: 'include', ...options });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Billing request failed');
  return payload;
};

const loadStripe = async () => {
  if (window.Stripe) return window.Stripe;
  if (!stripeScriptPromise) {
    stripeScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.dataset.platformaStripe = 'true';
      script.onload = () => resolve(window.Stripe);
      script.onerror = () => reject(new Error('Secure card input could not be loaded'));
      document.head.appendChild(script);
    });
  }
  return stripeScriptPromise;
};

const destroyElements = () => {
  [cardNumber, cardExpiry, cardCvc].forEach((element) => element?.destroy?.());
  cardNumber = null;
  cardExpiry = null;
  cardCvc = null;
  elements = null;
  stripe = null;
  setupIntentId = '';
  clientSecret = '';
  ready.value = false;
};

const initializeStripe = async () => {
  destroyElements();
  errorMessage.value = '';
  try {
    const intent = await requestJson('/stripe/setup-intent', { method: 'POST' });
    const Stripe = await loadStripe();
    if (!Stripe || !intent.publishableKey || !intent.clientSecret) throw new Error('Stripe billing is not configured');
    await nextTick();
    stripe = Stripe(intent.publishableKey);
    elements = stripe.elements();
    const style = { base: { color: '#18181b', fontSize: '16px', '::placeholder': { color: '#a1a1aa' } } };
    cardNumber = elements.create('cardNumber', { style });
    cardExpiry = elements.create('cardExpiry', { style });
    cardCvc = elements.create('cardCvc', { style });
    cardNumber.mount(cardNumberMount.value);
    cardExpiry.mount(cardExpiryMount.value);
    cardCvc.mount(cardCvcMount.value);
    setupIntentId = intent.setupIntentId;
    clientSecret = intent.clientSecret;
    ready.value = true;
  } catch (error) {
    errorMessage.value = error?.message || 'Secure card input could not be loaded';
  }
};

const initialize = async () => {
  destroyElements();
  initializing.value = true;
  errorMessage.value = '';
  try {
    const processor = await requestJson('/payment-provider');
    provider.value = processor.activeProvider === 'boa' ? 'boa' : 'stripe';
    if (provider.value === 'boa' && !processor.bankOfAmericaAvailable) throw new Error('Platforma Bank of America is not ready for card setup');
    if (provider.value === 'stripe' && !processor.stripeAvailable) throw new Error('Platforma Stripe is not ready for card setup');
  } catch (error) {
    errorMessage.value = error?.message || 'Secure card input could not be loaded';
  } finally {
    initializing.value = false;
  }
  if (!errorMessage.value && provider.value === 'stripe') {
    await nextTick();
    await initializeStripe();
  }
};

const finishProvider = (payload) => {
  emit('saved', payload);
  emit('update:open', false);
  window.dispatchEvent(new CustomEvent('platforma:creator-subscription-updated'));
};

const handleBoaSaved = (payload) => {
  finishProvider(payload);
};

const saveCard = async () => {
  if (!ready.value || submitting.value) return;
  submitting.value = true;
  errorMessage.value = '';
  try {
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardNumber, billing_details: { name: cardholderName.value || undefined } },
    });
    if (result.error) throw new Error(result.error.message || 'Please check your card details');
    const payload = await requestJson('/stripe/setup-intent/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupIntentId: result.setupIntent?.id || setupIntentId }),
    });
    finishProvider(payload);
  } catch (error) {
    errorMessage.value = error?.message || 'Card update failed';
  } finally {
    submitting.value = false;
  }
};

const close = () => {
  if (!submitting.value) emit('update:open', false);
};

watch(() => props.open, (isOpen) => {
  if (isOpen) void initialize();
  else destroyElements();
});
onBeforeUnmount(destroyElements);
</script>
